// src/pages/TermsOfService.js
// Terms of Service for XADevs (Company Website)

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.4;
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem var(--spacing-md) 4rem;
  position: relative;
  z-index: 1;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  transition: all var(--transition-fast);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-decoration: none;
  margin-bottom: var(--spacing-lg);

  svg {
    margin-right: var(--spacing-xs);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--white);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
`;

const TermsContainer = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  line-height: 1.8;

  h2 {
    color: var(--primary);
    margin-top: var(--spacing-lg);
    margin-bottom: var(--spacing-md);
    font-size: 1.5rem;
  }

  h3 {
    color: var(--secondary);
    margin-top: var(--spacing-md);
    margin-bottom: var(--spacing-sm);
    font-size: 1.2rem;
  }

  p {
    color: var(--text-secondary);
    margin-bottom: var(--spacing-md);
  }

  ul, ol {
    color: var(--text-secondary);
    margin-bottom: var(--spacing-md);
    padding-left: var(--spacing-lg);
  }

  li {
    margin-bottom: var(--spacing-xs);
  }

  a {
    color: var(--accent);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  strong {
    color: var(--text-primary);
  }
`;

const TermsOfService = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO routeKey="termsOfService" />
      <PageContainer>
        <ContentWrapper>
        <BackButton to="/">
          <FiChevronLeft /> Back to Home
        </BackButton>

        <PageHeader>
          <PageTitle>Terms of Service</PageTitle>
          <LastUpdated>Last Updated: January 2026</LastUpdated>
        </PageHeader>

        <TermsContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>1. Acceptance of Terms</h2>
          <p>
            Welcome to XADevs LLC. By accessing or using our website at <a href="https://xadevs.com">xadevs.com</a> ("Website"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Website.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            XADevs LLC ("we," "our," or "us") is a software company that designs, builds, and operates AI-powered products, cloud software systems, and cross-platform applications. Our Website provides information about our company, our products, and allows users to:
          </p>
          <ul>
            <li>Learn about our products and software systems</li>
            <li>Subscribe to our newsletter</li>
            <li>Contact our support team</li>
            <li>Access product-specific information and policies</li>
          </ul>

          <h2>3. User Responsibilities</h2>
          <p>When using our Website, you agree to:</p>
          <ul>
            <li>Provide accurate and truthful information</li>
            <li>Not use the Website for any illegal or unauthorized purpose</li>
            <li>Not transmit any viruses, malware, or harmful code</li>
            <li>Not attempt to gain unauthorized access to our systems</li>
            <li>Not engage in any activity that disrupts or interferes with the Website</li>
            <li>Not scrape, harvest, or collect information from the Website using automated means</li>
          </ul>

          <h2>4. Intellectual Property Rights</h2>

          <h3>4.1 Our Content</h3>
          <p>
            All content on this Website, including but not limited to text, graphics, logos, images, software, and design, is the property of XADevs LLC and is protected by United States and international copyright, trademark, and other intellectual property laws.
          </p>

          <h3>4.2 Restrictions</h3>
          <p>You may not:</p>
          <ul>
            <li>Copy, modify, distribute, or reproduce any content from the Website without our written permission</li>
            <li>Use our trademarks, logos, or brand names without authorization</li>
            <li>Create derivative works based on our content</li>
            <li>Reverse engineer or attempt to extract source code from our Website</li>
          </ul>

          <h2>5. Product-Specific Terms</h2>
          <p>
            Our individual products and applications have their own specific Terms and Conditions. By using our products, you also agree to their respective terms:
          </p>
          <ul>
            <li><a href="/volpetrack/terms">VolpeTrack Terms & Conditions</a></li>
          </ul>

          <h2>6. Newsletter and Communications</h2>
          <p>
            By subscribing to our newsletter, you consent to receive marketing communications from us. You may unsubscribe at any time by clicking the unsubscribe link in our emails or contacting us at <a href="mailto:hello@xadevs.com">hello@xadevs.com</a>.
          </p>

          <h2>7. Disclaimer of Warranties</h2>
          <p>
            THE WEBSITE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul>
            <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
            <li>Warranties that the Website will be uninterrupted, secure, or error-free</li>
            <li>Warranties regarding the accuracy, reliability, or completeness of content</li>
          </ul>

          <h2>8. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, XADEVS LLC SHALL NOT BE LIABLE FOR ANY:
          </p>
          <ul>
            <li>Indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, revenue, data, or use</li>
            <li>Damages arising from your use or inability to use the Website</li>
            <li>Damages resulting from unauthorized access to your data</li>
          </ul>
          <p>
            Our total liability to you for any claims arising from your use of the Website shall not exceed $100 USD.
          </p>

          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless XADevs LLC, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising from:
          </p>
          <ul>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Your use or misuse of the Website</li>
          </ul>

          <h2>10. Third-Party Links</h2>
          <p>
            Our Website may contain links to third-party websites. We are not responsible for the content, terms, or privacy practices of these external sites. Your use of third-party websites is at your own risk.
          </p>

          <h2>11. Privacy</h2>
          <p>
            Your use of our Website is also governed by our <a href="/privacy-policy">Privacy Policy</a>, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.
          </p>

          <h2>12. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of material changes by updating the "Last Updated" date at the top of this page. Your continued use of the Website after such changes constitutes your acceptance of the new Terms.
          </p>

          <h2>13. Termination</h2>
          <p>
            We reserve the right to terminate or suspend your access to the Website at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
          </p>

          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of Wyoming, United States, without regard to its conflict of law provisions.
          </p>

          <h2>15. Dispute Resolution</h2>

          <h3>15.1 Informal Resolution</h3>
          <p>
            Before filing a claim, you agree to contact us at <a href="mailto:hello@xadevs.com">hello@xadevs.com</a> to attempt to resolve the dispute informally.
          </p>

          <h3>15.2 Arbitration</h3>
          <p>
            Any disputes arising from these Terms or your use of the Website shall be resolved through binding arbitration in Wyoming, United States, in accordance with the American Arbitration Association's rules.
          </p>

          <h2>16. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>

          <h2>17. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and any product-specific terms, constitute the entire agreement between you and XADevs LLC regarding your use of the Website.
          </p>

          <h2>18. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <ul>
            <li><strong>Company:</strong> XADevs LLC</li>
            <li><strong>Email:</strong> <a href="mailto:hello@xadevs.com">hello@xadevs.com</a></li>
            <li><strong>Address:</strong> 30 N Gould St #N, Sheridan, WY 82801, USA</li>
            <li><strong>Phone:</strong> +1 307 556 7770</li>
            <li><strong>Support:</strong> <a href="/support">Contact Support</a></li>
          </ul>
          </TermsContainer>
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default TermsOfService;
